import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Trash2, Upload, Image as ImageIcon, Video as VideoIcon, RefreshCw, CheckCircle2, ChevronRight, ChevronDown, Cloud, Music, RotateCcw } from "lucide-react";
import { MEDIA_TREE, flattenTree } from "@/content/media-tree";

function formatError(detail) {
  if (!detail) return "Erreur";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

// ============================================================
// Panneau Médiathèque — vue arborescente
// ============================================================
export default function MediaPanel({ site }) {
  const [uploaded, setUploaded] = useState([]);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState("");
  const [expanded, setExpanded] = useState(() => new Set(MEDIA_TREE.map((n) => n.id)));
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.listMedia(site).then(setUploaded);
  }, [site]);

  const uploadedMap = useMemo(
    () => new Map(uploaded.map((f) => [f.filename, f])),
    [uploaded]
  );

  const stats = useMemo(() => {
    const seen = new Set();
    const list = flattenTree(MEDIA_TREE).filter((m) => {
      if (seen.has(m.filename)) return false;
      seen.add(m.filename);
      return true;
    });
    return {
      total: list.length,
      images: list.filter((m) => m.type === "image").length,
      videos: list.filter((m) => m.type === "video").length,
      audios: list.filter((m) => m.type === "audio").length,
      overridden: list.filter((m) => !m.cdn && uploadedMap.has(m.filename)).length,
      cdn: list.filter((m) => m.cdn).length,
    };
  }, [uploadedMap]);

  const expandedIdsForSearch = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    const ids = new Set();
    const walk = (node) => {
      const labelHit = (node.label || "").toLowerCase().includes(q);
      const mediaHit = (node.media || []).some(
        (m) =>
          (m.filename || "").toLowerCase().includes(q) ||
          (m.alt || "").toLowerCase().includes(q)
      );
      const childHit = (node.children || []).some(walk);
      const hit = labelHit || mediaHit || childHit;
      if (hit) ids.add(node.id);
      return hit;
    };
    MEDIA_TREE.forEach(walk);
    return ids;
  }, [query]);

  const isExpanded = (id) =>
    expandedIdsForSearch ? expandedIdsForSearch.has(id) : expanded.has(id);

  const toggle = (id) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const expandAll = () => {
    const all = new Set();
    const walk = (n) => {
      all.add(n.id);
      (n.children || []).forEach(walk);
    };
    MEDIA_TREE.forEach(walk);
    setExpanded(all);
  };

  const collapseAll = () => setExpanded(new Set());

  const doReplace = (filename) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(filename);
    setMsg("");
    try {
      await api.uploadMedia(file, filename, site);
      setMsg("✓ " + filename + " remplacé");
      const list = await api.listMedia(site);
      setUploaded(list);
    } catch (err) {
      setMsg("Erreur : " + formatError(err.response?.data?.detail));
    } finally {
      setBusy(null);
      e.target.value = "";
    }
  };

  const doRestore = async (filename) => {
    if (!window.confirm("Restaurer l'original pour " + filename + " ?")) return;
    setBusy(filename);
    try {
      await api.delMedia(filename);
      setMsg("↺ " + filename + " restauré");
      const list = await api.listMedia(site);
      setUploaded(list);
    } catch (err) {
      setMsg("Erreur : " + formatError(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  // Aplatit le rendu de l'arbre en liste de "lignes" pour éviter la récursion JSX
  const flatRows = useMemo(() => buildFlatRows(MEDIA_TREE, isExpanded), [isExpanded, expanded, expandedIdsForSearch]);

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-heading text-2xl font-light text-refuge-text mb-1">
          Médiathèque <span className="text-refuge-soft text-lg">· arborescence du site</span>
        </h2>
        <p className="font-body text-xs text-refuge-soft italic">
          {stats.total} médias · {stats.images} images · {stats.videos} vidéos
          {stats.audios ? " · " + stats.audios + " audio" : ""} · {stats.overridden} remplacé{stats.overridden > 1 ? "s" : ""} · {stats.cdn} CDN
        </p>
      </div>

      {msg && (
        <div className="mb-4 px-4 py-2.5 rounded-lg bg-refuge-ruby/10 border border-refuge-ruby/30 text-sm text-refuge-ruby" data-testid="media-msg">
          {msg}
        </div>
      )}

      <div className="mb-6 flex gap-3">
        <input
          type="text"
          placeholder="Rechercher : nom de fichier, page, description…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1 px-4 py-2 rounded-full border border-refuge-border bg-white font-body text-sm text-refuge-text focus:outline-none focus:border-refuge-ruby"
          data-testid="media-search"
        />
        <button
          type="button"
          onClick={expandAll}
          className="px-4 py-2 rounded-full border border-refuge-border text-refuge-soft hover:text-refuge-ruby hover:border-refuge-ruby font-body text-xs transition-all"
          data-testid="media-expand-all"
        >
          Tout déplier
        </button>
        <button
          type="button"
          onClick={collapseAll}
          className="px-4 py-2 rounded-full border border-refuge-border text-refuge-soft hover:text-refuge-ruby hover:border-refuge-ruby font-body text-xs transition-all"
          data-testid="media-collapse-all"
        >
          Tout replier
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-refuge-border overflow-hidden">
        {flatRows.map((row, idx) => (
          <TreeRow
            key={row.kind + "-" + row.id + "-" + idx}
            row={row}
            uploadedMap={uploadedMap}
            busy={busy}
            onToggle={toggle}
            onReplace={doReplace}
            onRestore={doRestore}
          />
        ))}
      </div>
    </div>
  );
}

// Convertit l'arbre en une liste plate de lignes à rendre. Évite la
// récursion JSX qui fait planter babel-traverse.
function buildFlatRows(tree, isExpandedFn) {
  const rows = [];
  const walk = (node, depth) => {
    const expandable =
      (node.children && node.children.length > 0) ||
      (node.media && node.media.length > 0) ||
      !!node.note;
    const open = isExpandedFn(node.id);
    rows.push({
      kind: "node",
      id: node.id,
      label: node.label,
      depth,
      expandable,
      open,
      mediaCount: node.media ? node.media.length : 0,
    });
    if (open) {
      if (node.note) {
        rows.push({ kind: "note", id: node.id, depth, text: node.note });
      }
      if (node.media && node.media.length > 0) {
        rows.push({ kind: "media", id: node.id, depth, media: node.media });
      }
      if (node.children) {
        node.children.forEach((c) => walk(c, depth + 1));
      }
    }
  };
  tree.forEach((n) => walk(n, 0));
  return rows;
}

function TreeRow({ row, uploadedMap, busy, onToggle, onReplace, onRestore }) {
  const indent = 16 + row.depth * 20;
  if (row.kind === "node") {
    return (
      <div className="border-b border-refuge-border/50">
        <button
          type="button"
          onClick={() => row.expandable && onToggle(row.id)}
          className={
            "w-full flex items-center gap-2 py-3 text-left transition-colors " +
            (row.expandable ? "hover:bg-refuge-bg/60" : "cursor-default")
          }
          style={{ paddingLeft: indent, paddingRight: 16 }}
          data-testid={"tree-node-" + row.id}
        >
          <span className="shrink-0 w-4 flex justify-center">
            {row.expandable ? (
              row.open ? <ChevronDown size={14} className="text-refuge-soft" strokeWidth={1.5} /> : <ChevronRight size={14} className="text-refuge-soft" strokeWidth={1.5} />
            ) : null}
          </span>
          <span
            className={
              "font-heading font-light " +
              (row.depth === 0
                ? "text-lg text-refuge-text"
                : row.depth === 1
                ? "text-base text-refuge-text"
                : "text-sm text-refuge-text/85 italic")
            }
          >
            {row.label}
          </span>
          {row.mediaCount > 0 && (
            <span className="ml-2 font-body text-[10px] tracking-[0.14em] uppercase text-refuge-soft">
              {row.mediaCount} média{row.mediaCount > 1 ? "s" : ""}
            </span>
          )}
        </button>
      </div>
    );
  }
  if (row.kind === "note") {
    return (
      <div className="pb-4 pt-1" style={{ paddingLeft: indent + 28, paddingRight: 16 }}>
        <p className="font-body text-xs italic text-refuge-soft leading-relaxed">{row.text}</p>
      </div>
    );
  }
  // media grid
  return (
    <div className="pb-3" style={{ paddingLeft: indent + 28, paddingRight: 16 }}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {row.media.map((m) => (
          <MediaCard
            key={row.id + "-" + m.filename}
            media={m}
            overridden={!m.cdn && uploadedMap.has(m.filename)}
            uploadedDate={uploadedMap.get(m.filename)?.uploadDate}
            busy={busy === m.filename}
            onReplace={onReplace(m.filename)}
            onRestore={() => onRestore(m.filename)}
          />
        ))}
      </div>
    </div>
  );
}

function MediaCard({ media, overridden, uploadedDate, busy, onReplace, onRestore }) {
  const isImg = media.type === "image";
  const isVid = media.type === "video";
  const isAud = media.type === "audio";
  const backendBase = process.env.REACT_APP_BACKEND_URL;
  const previewUrl = overridden
    ? backendBase + "/api/media/" + encodeURIComponent(media.filename) + "?v=" + (uploadedDate || Date.now())
    : media.url;

  return (
    <div className="rounded-lg border border-refuge-border bg-white overflow-hidden flex flex-col" data-testid={"media-card-" + media.filename}>
      <div className="relative bg-refuge-bg aspect-video overflow-hidden">
        <MediaPreview isImg={isImg} isVid={isVid} isAud={isAud} url={previewUrl} alt={media.alt} />
        <div className="absolute top-1.5 left-1.5 flex gap-1">
          <TypeBadge isImg={isImg} isVid={isVid} isAud={isAud} />
          {media.cdn && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-blue-500/90 text-white font-body text-[9px] tracking-wide" title="Hébergé sur un CDN externe — non remplaçable">
              <Cloud size={9} strokeWidth={2} /> CDN
            </span>
          )}
          {overridden && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-refuge-ruby text-white font-body text-[9px] tracking-wide">
              <CheckCircle2 size={9} strokeWidth={2} /> Remplacé
            </span>
          )}
        </div>
      </div>
      <div className="p-2.5 flex flex-col flex-1">
        <p className="font-mono text-[10px] text-refuge-soft mb-1 truncate" title={media.filename}>
          {media.filename}
        </p>
        <p className="font-body text-xs text-refuge-text mb-2.5 leading-snug line-clamp-2" title={media.alt}>
          {media.alt}
        </p>
        <MediaActions
          media={media}
          overridden={overridden}
          busy={busy}
          isImg={isImg}
          isVid={isVid}
          isAud={isAud}
          onReplace={onReplace}
          onRestore={onRestore}
        />
      </div>
    </div>
  );
}

function MediaPreview({ isImg, isVid, isAud, url, alt }) {
  if (isImg) {
    return (
      <img
        src={url}
        alt={alt}
        loading="lazy"
        className="w-full h-full object-cover"
        onError={(e) => {
          e.currentTarget.style.opacity = "0.2";
        }}
      />
    );
  }
  if (isVid) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-refuge-text/5">
        <VideoIcon className="w-6 h-6 text-refuge-soft" strokeWidth={1.2} />
        <p className="font-body text-[10px] text-refuge-soft">Vidéo</p>
      </div>
    );
  }
  if (isAud) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-refuge-text/5">
        <Music className="w-6 h-6 text-refuge-soft" strokeWidth={1.2} />
        <p className="font-body text-[10px] text-refuge-soft">Audio</p>
      </div>
    );
  }
  return null;
}

function TypeBadge({ isImg, isVid, isAud }) {
  return (
    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-white/95 text-refuge-text font-body text-[9px] tracking-wide">
      {isImg && <ImageIcon size={9} strokeWidth={1.4} />}
      {isVid && <VideoIcon size={9} strokeWidth={1.4} />}
      {isAud && <Music size={9} strokeWidth={1.4} />}
      {isImg ? "Photo" : isVid ? "Vidéo" : "Audio"}
    </span>
  );
}

function MediaActions({ media, overridden, busy, isImg, isVid, onReplace, onRestore }) {
  if (media.cdn) {
    return (
      <a
        href={media.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-full border border-refuge-border text-refuge-soft hover:text-refuge-ruby hover:border-refuge-ruby font-body text-[10px] tracking-wide transition-all"
        data-testid={"media-open-" + media.filename}
      >
        Voir l'original
      </a>
    );
  }
  const accept = isImg ? "image/*" : isVid ? "video/*" : "audio/*";
  return (
    <div className="mt-auto flex gap-1.5">
      <label
        className={
          "flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-full text-white font-body text-[10px] tracking-wide cursor-pointer transition-all " +
          (busy ? "bg-refuge-ruby/50 cursor-wait" : "bg-refuge-ruby hover:bg-refuge-ruby/90")
        }
        data-testid={"media-replace-" + media.filename}
      >
        {busy ? (
          <>
            <RefreshCw size={10} className="animate-spin" /> Upload…
          </>
        ) : (
          <>
            <Upload size={10} strokeWidth={1.5} /> Remplacer
          </>
        )}
        <input type="file" onChange={onReplace} className="hidden" accept={accept} disabled={busy} />
      </label>
      {overridden && (
        <button
          type="button"
          onClick={onRestore}
          title="Restaurer l'original"
          data-testid={"media-restore-" + media.filename}
          className="px-2 py-1.5 rounded-full border border-refuge-border text-refuge-soft hover:text-refuge-ruby hover:border-refuge-ruby transition-all"
        >
          <RotateCcw size={10} strokeWidth={1.5} />
        </button>
      )}
    </div>
  );
}
